import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../utils/apiConfig';

/**
 * TeamStatus - Widget "Mon équipe" pour Chef d'équipe
 *
 * Affiche le statut des membres du groupe avec sélecteur si multi-groupes.
 */
const TeamStatus = ({ token, onNavigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  useEffect(() => {
    const fetchTeamStatus = async () => {
      if (!token) return;

      setLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/dashboard/team-status`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (!response.ok) throw new Error('Erreur de chargement');

        const result = await response.json();
        if (result.success && result.data) {
          setData(result.data);
          // Sélectionner le premier groupe par défaut
          if (!selectedGroupId && result.data.groups?.length > 0) {
            setSelectedGroupId(result.data.groups[0].group_id);
          } else if (!selectedGroupId && result.data.group_id) {
            setSelectedGroupId(result.data.group_id);
          }
        } else {
          setData(null);
        }
      } catch (err) {
        console.error('Erreur fetch team status:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamStatus();
  }, [token]);

  if (loading) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Mon équipe</h4>
        <div style={styles.loading}>Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Mon équipe</h4>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  const groups = data?.groups || [];
  const hasGroups = groups.length > 0 || data?.group_id;

  if (!data || !hasGroups) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Mon équipe</h4>
        <div style={styles.empty}>
          <span style={styles.emptyIcon}>👥</span>
          <p style={styles.emptyText}>Aucun groupe assigné</p>
        </div>
      </div>
    );
  }

  // Données du groupe sélectionné
  const currentGroup = groups.find(g => g.group_id === selectedGroupId) || groups[0];
  const groupName = currentGroup?.group_name || data.group_name;
  const summary = currentGroup?.summary || data.summary;
  const hasMultipleGroups = groups.length > 1;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.title}>Mon équipe</h4>
        <button
          style={styles.viewButton}
          onClick={() => onNavigate && onNavigate('groups')}
        >
          Voir groupe
        </button>
      </div>

      <div style={styles.groupInfo}>
        <span style={styles.groupIcon}>🚛</span>
        <div style={{ flex: 1 }}>
          {hasMultipleGroups ? (
            <select
              value={selectedGroupId || ''}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              style={styles.groupSelect}
            >
              {groups.map(g => (
                <option key={g.group_id} value={g.group_id}>
                  {g.group_name}
                </option>
              ))}
            </select>
          ) : (
            <div style={styles.groupName}>{groupName}</div>
          )}
          <div style={styles.groupCount}>
            {summary?.total || 0} membre{(summary?.total || 0) > 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {summary && (
        <div style={styles.statsGrid}>
          <div style={{ ...styles.statItem, borderColor: '#28a745' }}>
            <span style={styles.statValue}>{summary.on_mission}</span>
            <span style={styles.statLabel}>En mission</span>
          </div>
          <div style={{ ...styles.statItem, borderColor: '#17a2b8' }}>
            <span style={styles.statValue}>{summary.available}</span>
            <span style={styles.statLabel}>Disponibles</span>
          </div>
          <div style={{ ...styles.statItem, borderColor: '#ffc107' }}>
            <span style={styles.statValue}>{summary.absent}</span>
            <span style={styles.statLabel}>Absents</span>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a2e'
  },
  viewButton: {
    background: 'none',
    border: 'none',
    color: '#6366f1',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500'
  },
  loading: {
    textAlign: 'center',
    padding: '30px',
    color: '#6c757d'
  },
  error: {
    padding: '20px',
    backgroundColor: '#fff5f5',
    borderRadius: '8px',
    color: '#dc3545',
    textAlign: 'center'
  },
  empty: {
    textAlign: 'center',
    padding: '30px'
  },
  emptyIcon: {
    fontSize: '2rem',
    display: 'block',
    marginBottom: '8px'
  },
  emptyText: {
    margin: 0,
    color: '#6c757d'
  },
  groupInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    marginBottom: '16px'
  },
  groupIcon: {
    fontSize: '2rem'
  },
  groupName: {
    fontWeight: '600',
    fontSize: '1rem',
    color: '#1a1a2e'
  },
  groupSelect: {
    fontWeight: '600',
    fontSize: '1rem',
    color: '#1a1a2e',
    background: 'white',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    padding: '4px 8px',
    cursor: 'pointer',
    width: '100%'
  },
  groupCount: {
    fontSize: '0.9rem',
    color: '#6c757d'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px'
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '12px 8px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    borderLeft: '3px solid'
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a1a2e'
  },
  statLabel: {
    fontSize: '0.8rem',
    color: '#6c757d',
    marginTop: '4px'
  }
};

export default TeamStatus;
